/**
 * salesTrendDataSource.ts — WatermelonDB → SalesTrendInput bridge.
 *
 * Loads three windows of SALE + CREDIT_NOTE invoices (voided excluded): the
 * current range, the immediately-preceding equal-length window, and the same
 * window a year earlier. The pure builder aligns and bins them.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { Invoice, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import type { Granularity, SalesTrendInput, TrendInvoiceInput } from './salesTrend';

async function loadInvoices(storeId: string, from: Date, to: Date): Promise<TrendInvoiceInput[]> {
  const rows = await database.get<Invoice>('invoices').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
    Q.where('type', Q.oneOf(['SALE', 'CREDIT_NOTE'])),
    Q.where('created_at', Q.gte(from.getTime())),
    Q.where('created_at', Q.lt(to.getTime())),
  ).fetch();
  return rows.map((i) => ({ type: i.type, totalAmount: i.totalAmount, date: i.createdAt }));
}

/** Shift a date back by whole calendar years (keeps month/day). */
function minusYears(d: Date, years: number): Date {
  return new Date(d.getFullYear() - years, d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds());
}

export async function loadSalesTrend(
  storeId: string,
  from: Date,
  to: Date,
  granularity: Granularity,
): Promise<SalesTrendInput> {
  const span = to.getTime() - from.getTime();
  const prevFrom = new Date(from.getTime() - span);
  const lastYearFrom = minusYears(from, 1);
  const lastYearTo = minusYears(to, 1);

  const [current, previous, lastYear] = await Promise.all([
    loadInvoices(storeId, from, to),
    loadInvoices(storeId, prevFrom, from),
    loadInvoices(storeId, lastYearFrom, lastYearTo),
  ]);

  return { current, previous, lastYear, from, to, granularity };
}
